import { Alert, Button, Drawer, Stack, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { BOTTOM_BAR_HEIGHT } from '/@/components/app-navbar/bottombar';
import DashboardCard from '/@/components/bayer/dashboard-card';
import ModalV2 from '/@/components/bayer/modal-v2';
import Tabs from '/@/components/bayer/tabs';
import { FinalizeCampaignModal } from '/@/components/crop-monitoring/harvest-alert';
import PreceonBadge, { PRECEON_COLOR } from '/@/components/crop-monitoring/preceon-badge';
import Icon from '/@/components/icon';
import useUnitProvider from '/@/core/context/unit';
import { useCornCropMonitoringAlertConditions } from '/@/core/crop-monitoring';
import { useAllCatalogProducts } from '/@/hooks/catalog';
import { useMutateArchiveCropMonitoringById } from '/@/hooks/crop-monitoring/use-crop-monitoring';
import { useFieldById } from '/@/hooks/fields';
import { useGetUserNode } from '/@/hooks/nodes';
import { useHasWriteAccess } from '/@/hooks/session';
import { _ } from '/@/locale';
import { getCatalogBrand } from '/@/store/catalog/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { HistoricalWeather } from '/@/types/weather/forecast';
import { formatDate } from '/@/utils';
import { cropMonitoringPendingAsyncState, getHybridProductName } from '/@/utils/crop-monitoring';
import useIsMobile from '/@/utils/hooks/is-mobile';
import { reverse } from '/@/utils/router';
import NextVisitModal from '/@/views/dashboard/grower/components/next-visit-modal';
import YieldEstimationGuideModal from '/@/views/fields/field-monitoring/components/yield-estimation-guide-modal';
import { flowNavigationState, startVisitReportFlow } from '/@/views/visit-reports/flow-tracking';
import CreateReportModal from '../create-report-modal';
import CropMonitoringDialog from '../creation-dialog';
import FieldCurrentStatus from '../current-status';
import YieldEstimationForm from '../yield-estimation-form';
import FieldSummaryCardMobile from './components/field-summary-card-mobile';
import StageInfoMessage from './components/stage-info-message';
import Registries from './registries';
import AddRegistryModal from './registries/components/add';
import { type FieldRegistryFlow, FieldRegistryFlowContext } from './registries/components/add/flow-tracking';
import type { RegistryAddStep } from './registries/components/add/types';

const FieldMonitoringDetail = ({
    fieldId,
    cropMonitoring,
    historicalWeather,
    loadingWeather,
}: {
    fieldId: number;
    cropMonitoring?: CropMonitoring;
    historicalWeather: HistoricalWeather;
    loadingWeather: boolean;
}) => {
    const units = useUnitProvider();
    const fieldQuery = useFieldById({ id: fieldId });
    const isMobile = useIsMobile();
    const country = useSelector(getSessionCountry);
    const cropProtectionBrand = useSelector((state) => getCatalogBrand(state, 'crop_protection'));
    const cropProtectionProductsQuery = useAllCatalogProducts({
        brand: cropProtectionBrand?.name,
        crop: 'crop_protection',
        country: country!.code!,
        cropProtectionCrop: undefined,
        cropProtectionProductType: undefined,
    });
    const cropProtectionProductsEnabled = cropProtectionBrand && cropProtectionProductsQuery.data?.results?.length > 0;

    const [yieldEstimationDrawerIsOpen, setYieldEstimationDrawerIsOpen] = useState(false);
    const [yieldEstimationStep, setYieldEstimationStep] = useState<'form' | 'result'>('form');
    const [yieldEstimationId, setYieldEstimationId] = useState<number | null>(null);
    const [yieldEstimationDirty, setYieldEstimationDirty] = useState(false);
    const [closeModalIsOpen, setCloseModalIsOpen] = useState(false);
    const [CMFormModalIsOpen, setCMFormModalIsOpen] = useState(false);
    const [createReportModalIsOpen, setCreateReportModalIsOpen] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [addModalInitialStep, setAddModalInitialStep] = useState<RegistryAddStep>('select');
    const [nextVisitModalOpen, setNextVisitModalOpen] = useState(false);
    const [mobileTab, setMobileTab] = useState<'seguimiento' | 'estado'>('seguimiento');
    const [guideModalIsOpen, setGuideModalIsOpen] = useState(false);
    const [finalizeCampaignModalIsOpen, setFinalizeCampaignModalIsOpen] = useState(false);
    const [harvestReportCropMonitoring, setHarvestReportCropMonitoring] = useState<CropMonitoring | undefined>(
        undefined
    );
    const navigate = useNavigate();
    const grower = useGetUserNode(fieldQuery.data?.userNode ?? 0);
    const today = new Date();
    const plantingDate = cropMonitoring ? new Date(cropMonitoring.plantingDate) : null;
    const theme = useTheme();

    // Corn crop-monitoring created but not processed yet: exists, planting date passed, but
    // the backend GDU/phenology job hasn't run (`accumulatedGduDate` unset). Keyed on that
    // rather than on `phenology`, which the job can legitimately leave unset for a CM
    // processed right after planting — that would pin the state on forever. Drives both the
    // "preparando los datos" Alert and hiding the stage-info message (which has nothing
    // meaningful to show until then). Kept in sync with `FieldCurrentStatus`.
    const isCornPhenologyProcessing =
        cropMonitoring?.crop === 'corn' &&
        cropMonitoringPendingAsyncState(cropMonitoring) &&
        plantingDate != null &&
        today.getTime() > plantingDate.getTime();

    const title = useMemo(() => {
        const options = {
            corn: _('Híbrido sembrado'),
        };
        return options[cropMonitoring?.crop as keyof typeof options] || _('Variedad sembrada'); // for wheat and soy
    }, [cropMonitoring?.crop]);

    // Multi-hybrid (CUL-1505, approach B): each hybrid is its own active CropMonitoring of the
    // campaign. Cards show one value per hybrid (via children) when there are several; a single
    // hybrid keeps develop's `number` layout unchanged.
    const activeHybridCms = fieldQuery.data?.activeCropMonitorings ?? [];
    const hybridsList =
        activeHybridCms.length > 0
            ? activeHybridCms.filter((cm) => !cropMonitoring || cm.campaign === cropMonitoring.campaign)
            : cropMonitoring
              ? [cropMonitoring]
              : [];
    const isMultiHybrid = hybridsList.length > 1;
    const isSinglePreceon = hybridsList.length === 1 && !!hybridsList[0]?.cornCropMonitoring?.isPreceon;
    const fmtDensity = (v?: number | null) =>
        v ? units.toDensity(v, cropMonitoring?.crop ?? 'corn', 2, 'planting', true).toString() : '-';
    const fmtEnv = (v?: number | null) =>
        v != null ? units.toYield(v, cropMonitoring?.crop ?? 'corn', 2).toString() : '-';

    const canEditResource = useHasWriteAccess();
    const archiveCropMonitoringByIdMutation = useMutateArchiveCropMonitoringById(cropMonitoring);
    const alertConditions = useCornCropMonitoringAlertConditions({ cropMonitoring });

    // The attempt "Agregar registro" hands to Cosecha or Estimación, which open outside its modal.
    // Any other entry opens them without one (CUL-1632).
    const [harvestFlow, setHarvestFlow] = useState<FieldRegistryFlow>();
    const [yieldEstimationFlow, setYieldEstimationFlow] = useState<FieldRegistryFlow>();

    const openHarvestReport = useCallback(
        (targetCropMonitoring?: CropMonitoring, flow?: FieldRegistryFlow) => {
            // Defaults to the field's active crop monitoring — Barbecho's "Obtener reporte"
            // passes the previous (closed) campaign explicitly instead, since there may be no
            // active one at all.
            setHarvestReportCropMonitoring(targetCropMonitoring ?? cropMonitoring);
            setHarvestFlow(flow);
            setCreateReportModalIsOpen(true);
        },
        [cropMonitoring]
    );

    const openAddModal = useCallback((initialStep: RegistryAddStep = 'select') => {
        setAddModalInitialStep(initialStep);
        setAddModalOpen(true);
    }, []);

    const openYieldEstimation = useCallback(
        (step: 'form' | 'result' = 'form', estimationId: number | null = null, flow?: FieldRegistryFlow) => {
            setYieldEstimationStep(step);
            setYieldEstimationId(estimationId);
            setYieldEstimationFlow(flow);
            setYieldEstimationDrawerIsOpen(true);
        },
        []
    );

    // Confirm before closing only when the yield-estimation form has unsaved changes.
    const requestCloseYieldEstimation = useCallback(() => {
        if (yieldEstimationDirty) {
            setCloseModalIsOpen(true);
        } else {
            setYieldEstimationDrawerIsOpen(false);
        }
    }, [yieldEstimationDirty]);

    const openRecommendHerbicide = useCallback(() => {
        mpTrackEvent('Crop Protection Recommendation began', {
            entry_source: 'field-detail-fallow',
            crop: cropMonitoring?.crop,
            cp_type: 'weed',
        });
        navigate(
            reverse('cropProtectionRecommendation', {
                qs: {
                    fieldId,
                    crop: cropMonitoring?.crop,
                    type: 'weeds',
                    plantingDate: cropMonitoring?.plantingDate,
                    userNodeId: fieldQuery.data?.userNode,
                },
            })
        );
    }, [navigate, fieldId, cropMonitoring?.crop, cropMonitoring?.plantingDate, fieldQuery.data?.userNode]);

    // Goes straight to the editor with this lote, so the report's attempt starts here.
    const openCreateVisitReport = useCallback(() => {
        navigate(reverse('visitReport.VisitReportEdit', { qs: { fieldIds: String(fieldId) } }), {
            state: flowNavigationState(startVisitReportFlow('field-detail')),
        });
    }, [navigate, fieldId]);

    const openRequestNSmart = useCallback(() => {
        mpTrackEvent('N-Smart Recommendation began', { entry_source: 'crop-monitoring-stage-alert' });
        navigate(
            reverse('nitrogenRecommendation.NitrogenRecommendationCreate', {
                qs: { cropMonitoringId: cropMonitoring?.id },
            })
        );
    }, [navigate, cropMonitoring?.id]);

    const openGenerateRecommendation = useCallback(() => {
        mpTrackEvent('Crop Protection Recommendation began', {
            entry_source: 'crop-monitoring-stage-alert',
            crop: cropMonitoring?.crop,
        });
        navigate(
            reverse('cropProtectionRecommendation', {
                qs: {
                    fieldId,
                    crop: cropMonitoring?.crop,
                    plantingDate: cropMonitoring?.plantingDate,
                    userNodeId: fieldQuery.data?.userNode,
                },
            })
        );
    }, [navigate, fieldId, cropMonitoring?.crop, cropMonitoring?.plantingDate, fieldQuery.data?.userNode]);

    const openConfirmNSmartDate = useCallback(() => {
        navigate(
            reverse('nitrogenRecommendation.NitrogenRecommendationDetail', {
                params: { id: cropMonitoring?.nitrogenRecomendations?.[0] as number | undefined },
            })
        );
    }, [navigate, cropMonitoring?.nitrogenRecomendations]);

    const openRecommendFungicide = useCallback(() => {
        const trackedCrop =
            cropMonitoring?.crop === 'corn' || cropMonitoring?.crop === 'soy' ? cropMonitoring.crop : undefined;
        mpTrackEvent('Crop Protection Recommendation began', {
            entry_source: 'crop-monitoring-stage-alert',
            crop: trackedCrop,
            cp_type: 'disease',
        });
        navigate(
            reverse('cropProtectionRecommendation', {
                qs: {
                    fieldId,
                    crop: cropMonitoring?.crop,
                    type: 'diseases',
                    plantingDate: cropMonitoring?.plantingDate,
                    userNodeId: fieldQuery.data?.userNode,
                },
            })
        );
    }, [navigate, fieldId, cropMonitoring?.crop, cropMonitoring?.plantingDate, fieldQuery.data?.userNode]);

    return (
        <>
            {fieldQuery.data && !isCornPhenologyProcessing && (
                <StageInfoMessage
                    cropMonitoring={cropMonitoring}
                    field={fieldQuery.data}
                    onOpenHarvestReport={openHarvestReport}
                    onRegisterApplication={() => openAddModal('application')}
                    onRecommendHerbicide={openRecommendHerbicide}
                    onCreateVisitReport={openCreateVisitReport}
                    onScheduleVisit={() => setNextVisitModalOpen(true)}
                    onRequestNSmart={openRequestNSmart}
                    onGenerateRecommendation={openGenerateRecommendation}
                    onConfirmNSmartDate={openConfirmNSmartDate}
                    onRecommendFungicide={openRecommendFungicide}
                    onViewSamplingGuide={() => setGuideModalIsOpen(true)}
                    onEstimateYield={() => openYieldEstimation('form')}
                    onFinalizeCampaignWithoutData={() => setFinalizeCampaignModalIsOpen(true)}
                    onArchiveCropMonitoring={() => archiveCropMonitoringByIdMutation.trigger()}
                />
            )}
            {isMobile && (
                <FieldSummaryCardMobile
                    field={fieldQuery.data}
                    cropMonitoring={cropMonitoring}
                    units={units}
                    canEditResource={canEditResource}
                    onAddCrop={() => setCMFormModalIsOpen(true)}
                />
            )}
            {!isMobile && (
                <Grid2 container direction='row' spacing={3} mx={'-12px !important'}>
                    <Grid2 xs={12} md={3} sx={{ height: 'auto' }}>
                        {cropMonitoring?.active ? (
                            <DashboardCard
                                data-testid='field-monitoring-detail-active-crop-card'
                                title={title}
                                subtitle={
                                    !isMultiHybrid && cropMonitoring?.plantedArea
                                        ? `${_('Superficie sembrada')}: ${units.toArea(cropMonitoring.plantedArea, 2).toString()}`
                                        : undefined
                                }
                                sx={isSinglePreceon ? { borderColor: PRECEON_COLOR } : undefined}
                                headerRightContent={
                                    isSinglePreceon ? (
                                        <PreceonBadge
                                            sx={{
                                                alignSelf: 'flex-start',
                                                mt: -2,
                                                mr: -2,
                                                borderRadius: '0 0 0 8px',
                                            }}
                                        />
                                    ) : undefined
                                }
                            >
                                {hybridsList.map((h) => (
                                    <Stack
                                        key={h.id}
                                        direction='row'
                                        spacing={1}
                                        alignItems='center'
                                        flexWrap='nowrap'
                                        justifyContent='space-between'
                                        useFlexGap
                                    >
                                        <Typography
                                            color='primary'
                                            variant={isMultiHybrid ? 'h6' : 'h4'}
                                            component='span'
                                        >
                                            {getHybridProductName(h)}
                                        </Typography>
                                        {/* Cancels DashboardCard's p: 2 so the badge reaches the card edge. */}
                                        {h.cornCropMonitoring?.isPreceon && !isSinglePreceon && (
                                            <PreceonBadge sx={{ mr: -2 }} />
                                        )}
                                    </Stack>
                                ))}
                            </DashboardCard>
                        ) : (
                            <DashboardCard
                                title={_('Próximo cultivo')}
                                data-testid='field-monitoring-detail-next-crop-card'
                                subtitle={_('Agregá el próximo cultivo para poder hacer un mejor seguimiento')}
                                buttonProps={{
                                    onClick: () => setCMFormModalIsOpen(true),
                                    startIcon: (
                                        <Icon
                                            type='agronomy'
                                            size='xxs'
                                            sx={{
                                                fill: canEditResource
                                                    ? theme.palette.common.white
                                                    : theme.palette.action.disabled,
                                            }}
                                            padding={false}
                                        />
                                    ),
                                    children: _('Agregar cultivo'),
                                    size: 'small',
                                    disabled: !canEditResource,
                                }}
                                titleVariant='subtitle1'
                            />
                        )}
                    </Grid2>
                    <Grid2 xs={12} md={3} sx={{ height: 'auto' }}>
                        {cropMonitoring?.harvestDate ? (
                            <DashboardCard title={_('Fecha de cosecha')}>
                                <Typography color='primary' variant='h4'>
                                    {formatDate(new Date(cropMonitoring.harvestDate))}
                                </Typography>
                            </DashboardCard>
                        ) : (
                            <DashboardCard
                                title={_('Fecha de siembra')}
                                subtitle={
                                    cropMonitoring
                                        ? undefined
                                        : _('Acá vas a ver la fecha de siembra una vez que comience la campaña')
                                }
                            >
                                {hybridsList.map((h) => (
                                    <Typography key={h.id} color='primary' variant={isMultiHybrid ? 'h6' : 'h4'}>
                                        {formatDate(new Date(h.plantingDate))}
                                    </Typography>
                                ))}
                            </DashboardCard>
                        )}
                    </Grid2>
                    <Grid2 xs={12} md={3} sx={{ height: 'auto' }}>
                        <DashboardCard
                            title={_('Densidad')}
                            data-testid='field-monitoring-detail-density-card'
                            subtitle={
                                cropMonitoring
                                    ? undefined
                                    : _('Acá vas a ver la densidad una vez que comience la siembra')
                            }
                            titleVariant='subtitle1'
                        >
                            {hybridsList.map((h) => (
                                <Typography key={h.id} color='primary' variant={isMultiHybrid ? 'h6' : 'h4'}>
                                    {fmtDensity(h.density)}
                                </Typography>
                            ))}
                        </DashboardCard>
                    </Grid2>
                    <Grid2 xs={12} md={3} sx={{ height: 'auto' }}>
                        <DashboardCard
                            title={_('Ambiente')}
                            data-testid='field-monitoring-detail-environment-card'
                            subtitle={
                                cropMonitoring
                                    ? undefined
                                    : _('Acá vas a ver el ambiente una vez que comience la campaña')
                            }
                        >
                            {hybridsList.map((h) => (
                                <Typography key={h.id} color='primary' variant={isMultiHybrid ? 'h6' : 'h4'}>
                                    {fmtEnv(h.environment)}
                                </Typography>
                            ))}
                        </DashboardCard>
                    </Grid2>
                </Grid2>
            )}

            {isCornPhenologyProcessing && (
                <Alert severity='info' sx={{ my: 4 }}>
                    {_('Estamos preparando los datos para que sigas el avance del cultivo en cada etapa.')}
                </Alert>
            )}

            {isMobile ? (
                <Stack spacing={2} sx={{ mt: 3, pb: `calc(${BOTTOM_BAR_HEIGHT} + 68px)` }}>
                    <Tabs
                        value={mobileTab}
                        setValue={(value) => setMobileTab(value as 'seguimiento' | 'estado')}
                        options={[
                            { label: _('Seguimiento'), value: 'seguimiento' },
                            { label: _('Estado actual del lote'), value: 'estado' },
                        ]}
                    />
                    {mobileTab === 'seguimiento' ? (
                        <Registries
                            fieldId={fieldId}
                            cropMonitoring={cropMonitoring}
                            onOpenAddModal={openAddModal}
                            onOpenYieldEstimation={openYieldEstimation}
                        />
                    ) : (
                        <FieldCurrentStatus
                            cropMonitoring={cropMonitoring}
                            historicalWeather={historicalWeather}
                            loadingWeather={loadingWeather}
                        />
                    )}
                </Stack>
            ) : (
                <Grid2 container spacing={3} sx={{ mt: 3 }} alignItems='stretch'>
                    <Grid2 xs={12} md={8}>
                        <Registries
                            fieldId={fieldId}
                            cropMonitoring={cropMonitoring}
                            onOpenAddModal={openAddModal}
                            onOpenYieldEstimation={openYieldEstimation}
                        />
                    </Grid2>
                    <Grid2 xs={12} md={4}>
                        <Stack spacing={2}>
                            <FieldCurrentStatus
                                cropMonitoring={cropMonitoring}
                                historicalWeather={historicalWeather}
                                loadingWeather={loadingWeather}
                            />
                        </Stack>
                    </Grid2>
                </Grid2>
            )}

            {isMobile && mobileTab === 'seguimiento' && (
                <Stack
                    direction='row'
                    justifyContent='center'
                    p={2}
                    sx={{
                        width: '100%',
                        left: 0,
                        borderTop: (t) => `1px solid ${t.palette.divider}`,
                        backgroundColor: 'background.paper',
                        position: 'fixed',
                        bottom: { xs: BOTTOM_BAR_HEIGHT, sm: 0 },
                    }}
                >
                    <Tooltip
                        title={
                            cropMonitoring?.result?.id || cropMonitoring?.cornCropMonitoring?.phenology === 'harvested'
                                ? _('Campaña finalizada: no se pueden agregar más registros')
                                : ''
                        }
                        arrow
                    >
                        <span style={{ width: '100%' }}>
                            <Button
                                variant='contained'
                                fullWidth
                                disabled={
                                    !!cropMonitoring?.result?.id ||
                                    cropMonitoring?.cornCropMonitoring?.phenology === 'harvested'
                                }
                                color='secondary'
                                onClick={() => openAddModal()}
                            >
                                {_('Agregar registro')}
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>
            )}

            {cropMonitoring && (
                <Drawer open={yieldEstimationDrawerIsOpen} onClose={requestCloseYieldEstimation} anchor='right'>
                    <FieldRegistryFlowContext.Provider value={yieldEstimationFlow}>
                        <YieldEstimationForm
                            cropMonitoring={cropMonitoring}
                            setYieldEstimationDrawerIsOpen={setYieldEstimationDrawerIsOpen}
                            onRequestClose={requestCloseYieldEstimation}
                            onDirtyChange={setYieldEstimationDirty}
                            open={yieldEstimationDrawerIsOpen}
                            initialStep={yieldEstimationStep}
                            estimationId={yieldEstimationId}
                        />
                    </FieldRegistryFlowContext.Provider>
                </Drawer>
            )}
            <ModalV2
                open={closeModalIsOpen}
                onClose={() => {
                    setCloseModalIsOpen(false);
                }}
                actions={
                    <>
                        <Button
                            variant='text'
                            color='secondary'
                            onClick={() => {
                                setCloseModalIsOpen(false);
                            }}
                        >
                            {_('Seguir editando')}
                        </Button>
                        <Button
                            variant='contained'
                            color='secondary'
                            onClick={() => {
                                setCloseModalIsOpen(false);
                                setYieldEstimationDrawerIsOpen(false);
                            }}
                        >
                            {_('Confirmar')}
                        </Button>
                    </>
                }
                title={_('¿Querés salir sin guardar?')}
                actionsProps={{ sx: { justifyContent: 'end' } }}
                maxWidth={'sm'}
            >
                <Typography color={'primary'} variant='body1'>
                    {_('Al cancelar, los datos ingresados no se guardarán y tendrás que volver a cargarlos.')}
                </Typography>
            </ModalV2>
            {harvestReportCropMonitoring && fieldQuery.data && (
                <FieldRegistryFlowContext.Provider value={harvestFlow}>
                    <CreateReportModal
                        field={fieldQuery.data}
                        cropMonitoring={harvestReportCropMonitoring}
                        isOpen={createReportModalIsOpen}
                        setIsOpen={setCreateReportModalIsOpen}
                    />
                </FieldRegistryFlowContext.Provider>
            )}
            {fieldQuery.data && (
                <CropMonitoringDialog
                    open={CMFormModalIsOpen}
                    setClose={() => setCMFormModalIsOpen(false)}
                    field={fieldQuery.data}
                    entry_source='field-detail'
                />
            )}
            <AddRegistryModal
                open={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                fieldId={fieldId}
                cropMonitoring={cropMonitoring}
                initialStep={addModalInitialStep}
                onHarvest={(flow) => openHarvestReport(undefined, flow)}
                onYieldEstimation={(flow) => openYieldEstimation('form', null, flow)}
            />
            <NextVisitModal
                open={nextVisitModalOpen}
                onClose={() => setNextVisitModalOpen(false)}
                growerName={grower.data?.name ?? ''}
                growerPhone={grower.data?.phone}
                growerId={fieldQuery.data?.userNode}
            />
            <YieldEstimationGuideModal open={guideModalIsOpen} onClose={() => setGuideModalIsOpen(false)} />
            {cropMonitoring && (
                <FinalizeCampaignModal
                    cropMonitoring={cropMonitoring}
                    open={finalizeCampaignModalIsOpen}
                    onClose={() => setFinalizeCampaignModalIsOpen(false)}
                    setOpenHarvestModal={openHarvestReport}
                    setCloseHarvestModal={() => setFinalizeCampaignModalIsOpen(false)}
                />
            )}
        </>
    );
};

export default FieldMonitoringDetail;
